import { Box, Button, ButtonGroup, useToast } from "@chakra-ui/react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { hasEvent } from "@equidefi/portals";
import { HighlightCard } from "@equidefi/portals/components/investments/HighlightCard";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import {
  canEditPaymentMethod,
  computeFeeBreakdown,
  isPaymentFailed,
  latestInvestorPayment,
  offeringHidesSecurityLabel,
  offeringIsTerminated,
  offeringToFeeRates,
} from "@equidefi/shared";
import { BannerAlert, Heading, Text } from "@equidefi/ui";

import { PaymentInfoCard } from "@/components/payment/PaymentInfoCard";
import ReviewCard, { ReviewContent, ReviewSection } from "@/components/workflow/ReviewCard";
import { WorkflowButton } from "@/components/workflow/WorkflowButton";
import { CurrencyFormatter } from "@/constants/format";
import { stripeAccountPromise, stripePromise } from "@/helpers/stripe";
import {
  useAcknowledgeWirePaymentMutation,
  useCancelUncompletePayments,
  useInitiatePaymentMutation,
  useInitiateWirePaymentMutation,
  useSetupManualInvestmentPayment,
} from "@/hooks/useInvestments";
import useWorkflowPosition from "@/hooks/useWorkflowPosition";
import { useWorkflowContext } from "@/pages/workflow/context/WorkflowContext";

import { STRIPE_ACCORDION_TYPES } from "@/pages/app/vault/VaultPayment";
import { WorkflowContainer } from "../WorkflowContainer";
import WorkflowStepError from "../WorkflowStepError";
import { PaymentAccordion } from "./PaymentAccordion";
import { PaymentStepContext } from "./context";

const DEFAULT_NEXT_PAGE = "address";

// The Stripe card/ACH section resolves its concrete sub-type from the
// PaymentElement onChange; every other section maps directly to a payment type.
const CATEGORY_PAYMENT_TYPE = {
  CRYPTO: "crypto",
  [STRIPE_ACCORDION_TYPES.STRIPE_WIRE]: "customer_balance",
  WIRE: "wire",
  CHECK: "check",
};

export const WorkflowPayment = () => {
  const { investment, offering, analytics, refetchInvestment } = useWorkflowContext();
  const hideSecurityLabel = offeringHidesSecurityLabel(offering);
  const navigate = useNavigate();
  const location = useLocation();
  const [changePayment, setChangePayment] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const cancelPayments = useCancelUncompletePayments(investment?.id);

  useWorkflowPosition(3);

  useEffect(() => {
    refetchInvestment();
  }, [refetchInvestment]);

  const setupManualPayment = useSetupManualInvestmentPayment(investment?.id);

  const settingsMap = useMemo(() => {
    const transferAgent = offering?.offering_contacts?.find((c) => c.type === "TRANSFER_AGENT");
    return {
      bank_name: offering?.payment_bank_name,
      account_name: offering?.payment_account_name,
      account_number: offering?.payment_account_number,
      routing_number: offering?.payment_routing_number,
      transfer_agent_name: transferAgent?.company,
      wireInstructions: offering?.manual_payment_wire_instructions,
      checkInstructions: offering?.manual_payment_check_instructions,
    };
  }, [offering]);

  const createPayment = useInitiatePaymentMutation(investment?.id);
  const createWirePayment = useInitiateWirePaymentMutation(investment?.id);
  const acknowledgeWirePayment = useAcknowledgeWirePaymentMutation(investment?.id);
  const toast = useToast();

  useDocumentTitle(["Payment", investment?.offering?.name, investment?.issuer?.name]);

  const [paymentCategory, setPaymentCategory] = useState(null);
  const [selectedPaymentType, setSelectedPaymentType] = useState(null);

  const { stripeDeferred } = useFlags();

  const investorPaysFee = Boolean(offering?.settings?.investor_pays_fee);

  const feeRates = useMemo(() => offeringToFeeRates(offering), [offering]);

  // The fee shown in the summary; null until the investor selects a method.
  // Uses the same shared helper the backend charges with, so they never diverge.
  const feeBreakdown = useMemo(
    () =>
      investorPaysFee && selectedPaymentType
        ? computeFeeBreakdown(feeRates, selectedPaymentType, Number(investment?.amount ?? 0))
        : null,
    [investorPaysFee, selectedPaymentType, feeRates, investment?.amount],
  );

  const [clientSecret, setClientSecret] = useState("");
  const [paymentIntentId, setPaymentIntentId] = useState("");
  const [wireInstructions, setWireInstructions] = useState(null);
  const [wirePaymentIntentId, setWirePaymentIntentId] = useState("");
  const [stripe, setStripe] = useState(null);

  const handleSectionChange = (type) => {
    setPaymentCategory(type);
    setSelectedPaymentType(type === STRIPE_ACCORDION_TYPES.STRIPE ? null : (CATEGORY_PAYMENT_TYPE[type] ?? null));
    if (type === STRIPE_ACCORDION_TYPES.STRIPE && stripeDeferred) ensureStripeLoaded();
    if (type === STRIPE_ACCORDION_TYPES.STRIPE && !stripeDeferred) setupStripePayment();
    if (type === STRIPE_ACCORDION_TYPES.STRIPE_WIRE) setupStripeWirePayment();
  };

  const ensureStripeLoaded = () => {
    if (!stripe) {
      if (offering.stripe_account_id) {
        setStripe(stripeAccountPromise(offering.stripe_account_id));
      } else {
        setStripe(stripePromise);
      }
    }
  };

  const setupStripePayment = async () => {
    if (clientSecret) return null;

    try {
      const clientSecretData = await createPayment.mutateAsync();
      ensureStripeLoaded();
      setClientSecret(clientSecretData.clientSecret);
      setPaymentIntentId(clientSecretData.id);
    } catch (e) {
      console.error(e);
    }
  };

  const setupStripeWirePayment = async () => {
    if (wireInstructions) return null;

    try {
      const result = await createWirePayment.mutateAsync();
      setWireInstructions(result?.wireInstructions ?? null);
      setWirePaymentIntentId(result?.id ?? "");
    } catch (e) {
      console.error(e);
      toast({
        status: "error",
        description: "Could not generate wire instructions.",
      });
    }
  };

  const goToNextStep = async () => {
    const returnTo = location.state?.returnTo;
    const { data: freshInv } = await refetchInvestment();
    const page = returnTo ?? (freshInv.payment_editable ? DEFAULT_NEXT_PAGE : "address");

    if (freshInv.expedited) {
      return navigate(`/offerings/${offering.slug}/${freshInv?.id}/agreement`);
    }

    navigate(`/offerings/${offering.slug}/${freshInv?.id}/${page}`);
  };

  const handleWireContinue = async () => {
    if (isProcessingPayment) return;
    setIsProcessingPayment(true);

    if (wirePaymentIntentId) {
      try {
        await acknowledgeWirePayment.mutateAsync(wirePaymentIntentId);
      } catch (e) {
        console.error(e);
        toast({
          status: "error",
          description: "Could not submit wire payment. Please try again.",
        });
        setIsProcessingPayment(false);
        return;
      }
    }

    try {
      await goToNextStep();
    } catch (e) {
      console.error(e);
      setIsProcessingPayment(false);
    }
  };

  const handlePayment = async () => {
    // Prevent double submission by checking if already processing
    if (isProcessingPayment) return;

    setIsProcessingPayment(true);

    try {
      analytics.track("payment", {
        amount: investment.amount,
        type: paymentCategory.toLowerCase(),
      });

      await setupManualPayment.mutateAsync({
        category: "MANUAL",
        payment_type: paymentCategory.toLowerCase(),
      });

      await goToNextStep();
    } catch (e) {
      console.error(e);
      setIsProcessingPayment(false);
    }
  };

  const latestPayment = latestInvestorPayment(investment?.payments);
  const canEditMethod = canEditPaymentMethod(investment);

  const isOfferingTerminated = offeringIsTerminated(offering);

  if (isOfferingTerminated) {
    return (
      <WorkflowStepError
        title="Payment"
        text="This offering has been terminated. No payment method changes can be made."
        buttonLabel="Offering Terminated"
        variant="error"
      />
    );
  }

  if (hasEvent(investment.event_history, "KYC", "SUBMITTED")) {
    return (
      <WorkflowStepError
        onResume={() => navigate(`/vault/investment/${investment.id}`)}
        title="Payment"
        text="Your payment has already been submitted. You can check the status of your payment in your portfolio. Would you like to go there now?"
        buttonLabel="Go to Portfolio"
      />
    );
  }

  if (investment?.payment_action_url && !changePayment) {
    return (
      <WorkflowContainer title="Payment">
        <BannerAlert
          status="info"
          description="Your chosen payment option requires confirmation. Please continue to submit your payment."
          my={4}
        />

        <ButtonGroup orientation="vertical" w="100%">
          <WorkflowButton
            onClick={() => {
              window.location.href = investment?.payment_action_url;
            }}>
            Continue
          </WorkflowButton>

          <Button
            onClick={async () => {
              await cancelPayments.mutateAsync();
              await refetchInvestment();
              setChangePayment(true);
            }}
            variant="outline">
            Change Payment Method
          </Button>
        </ButtonGroup>
      </WorkflowContainer>
    );
  }

  if (!canEditMethod && !changePayment && !isProcessingPayment) {
    return (
      <WorkflowContainer title="Payment">
        {latestPayment && (
          <PaymentInfoCard
            payment={latestPayment}
            description="A payment process has already been started. You can safely continue to the next step. The details of your payment can be viewed in your Portfolio."
            mt={6}
          />
        )}
        <WorkflowButton
          mt={6}
          onClick={() => {
            if (investment.expedited) {
              return navigate(`/offerings/${offering.slug}/${investment?.id}/agreement`);
            }
            navigate(`/offerings/${offering.slug}/${investment.id}/${DEFAULT_NEXT_PAGE}`);
          }}>
          Continue
        </WorkflowButton>
      </WorkflowContainer>
    );
  }

  return (
    <PaymentStepContext.Provider
      value={{
        paymentIntentId,
        clientSecret,
      }}>
      <WorkflowContainer title="Payment" buttonLabel="Pay" pb={10}>
        {Boolean(latestPayment) && (isPaymentFailed(latestPayment) || !canEditMethod) ? (
          <PaymentInfoCard payment={latestPayment} mt={6} mb="4" />
        ) : (
          <HighlightCard text={offering?.issuer?.name} title="Your investment in" />
        )}
        <Box>
          <ReviewCard title="Investment Amount">
            <ReviewSection page="subscription" returnTo="payment">
              <ReviewContent label="Amount">
                {CurrencyFormatter.format(investment.amount)}
                {investorPaysFee &&
                  (feeBreakdown
                    ? ` + ${CurrencyFormatter.format(feeBreakdown.investorFeeTotal)} fee`
                    : " + processing fee")}
              </ReviewContent>
              {!hideSecurityLabel && (
                <ReviewContent label={offering?.security_type_plural}>{investment?.quantity}</ReviewContent>
              )}
            </ReviewSection>
            {investorPaysFee && (
              <Text textStyle="body2" color="gray.600" mt={2}>
                Processing fee depends on the payment method used.
              </Text>
            )}
          </ReviewCard>
          <BannerAlert
            status="info"
            titleLayout="inline"
            title="Investment acceptance"
            description="It may take 7-10 business days for your investment to be accepted. If your investment is not accepted, you will be notified and your funds will be refunded."
            my={4}
          />
        </Box>

        <Heading fontWeight="bold">Select your payment method</Heading>
        <PaymentAccordion
          onSubmitManualPayment={handlePayment}
          onSectionChange={handleSectionChange}
          onWireContinue={handleWireContinue}
          wireInstructions={wireInstructions}
          isWireLoading={createWirePayment.isLoading}
          isWireContinueLoading={acknowledgeWirePayment.isLoading}
          stripe={stripe}
          clientSecret={clientSecret}
          isManualPaymentLoading={isProcessingPayment || setupManualPayment.isLoading}
          paymentSettings={settingsMap}
          investment={investment}
          offering={offering}
          onStripeMethodChange={setSelectedPaymentType}
          deferredIntent={stripeDeferred}
          refetchInvestment={refetchInvestment}
          analytics={analytics}
        />
      </WorkflowContainer>
    </PaymentStepContext.Provider>
  );
};
